import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EditorHeader } from "./EditorHeader";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  { value: "archived_newsletter", label: "Archived Newsletter" },
  { value: "pitch_deck", label: "Pitch Deck" },
] as const;

export const ArchivedFilesEditor = () => {
  const qc = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<string>(""); // optional; default applied on save
  const DEFAULT_CATEGORY = "archived_newsletter";

  const { data: files, isLoading } = useQuery({
    queryKey: ["archived-files", category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("downloadable_files")
        .select("*")
        .in("category", CATEGORY_OPTIONS.map((c) => c.value))
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const addFiles = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!selectedFiles.length) throw new Error("No files selected");

      const usedCategory = (category || DEFAULT_CATEGORY) as string;
      const titleInput = String(formData.get("title") || "");
      const descriptionInput = formData.get("description")
        ? String(formData.get("description"))
        : null;

      for (const file of selectedFiles) {
        const fileExt = file.name.split(".").pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("legal_documents")
          .upload(filePath, file);
        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage
          .from("legal_documents")
          .getPublicUrl(filePath);

        const title = titleInput || file.name.replace(/\.[^/.]+$/, "");

        const { error } = await supabase.from("downloadable_files").insert([
          {
            title,
            description: descriptionInput,
            file_url: publicUrlData.publicUrl,
            category: usedCategory,
          },
        ]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["archived-files"] });
      qc.invalidateQueries({ queryKey: ["downloadable-files"] });
      toast.success("Files uploaded");
      setSelectedFiles([]);
    },
    onError: (e: any) => toast.error(e.message || "Upload failed"),
  });

  const deleteFile = useMutation({
    mutationFn: async (id: string) => {
      const { data: fileData, error: fetchErr } = await supabase
        .from("downloadable_files")
        .select("file_url")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;
      const filePath = (fileData as any).file_url.split("/").pop();
      const { error: storageErr } = await supabase.storage
        .from("legal_documents")
        .remove([filePath]);
      if (storageErr) throw storageErr;
      const { error } = await supabase
        .from("downloadable_files")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["archived-files"] });
      toast.success("File deleted");
    },
    onError: (e: any) => toast.error(e.message || "Delete failed"),
  });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await addFiles.mutateAsync(new FormData(e.currentTarget));
      (e.target as HTMLFormElement).reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <EditorHeader
        title="Archived Newsletter / Pitch Decks"
        description="Upload and manage archived newsletters and pitch decks"
      />

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Add New Item</h3>
        <EditorForm onSubmit={onSubmit} isSubmitting={isSubmitting}>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Category (optional)</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              className="w-full rounded-md border border-input bg-background px-3 py-2"
            >
              <option value="">No category (defaults to Archived Newsletter)</option>
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <EditorField label="Title" name="title" />

          <div className="space-y-2">
            <Label className="text-sm font-medium">File</Label>
            <Input type="file" multiple onChange={(e) => e.target.files && setSelectedFiles(Array.from(e.target.files))} required />
          </div>

          <EditorField label="Description" name="description" type="textarea" />

          <Button type="submit" className="w-full">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
        </EditorForm>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Current Items</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <div className="space-y-4">
            {files?.map((file: any) => (
              <div key={file.id} className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {CATEGORY_OPTIONS.find((c) => c.value === file.category)?.label}
                  </p>
                  <h4 className="font-medium">{file.title}</h4>
                  {file.description && (
                    <p className="text-sm text-muted-foreground">{file.description}</p>
                  )}
                </div>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={() => deleteFile.mutate(file.id)}
                  aria-label={`Delete ${file.title}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
